import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatTimeShort } from "@/lib/format";
import type { FiscalRow } from "@/types";
import { FLAG_LABEL, METHOD_LABEL, confirmationPdfUrl, hasConfirmation, isConfirmationMissing } from "./fiscal-labels";

export interface RowHandlers {
  onToggle: (items: { source: FiscalRow["source"]; sourceId: string }[], fiscalized: boolean) => void;
  onResend: (row: FiscalRow) => void;
}

export function FiscalCheck({ row, onToggle }: { row: FiscalRow } & Pick<RowHandlers, "onToggle">) {
  const done = row.fiscalizedAt !== null;
  return (
    <Checkbox
      checked={done}
      aria-label={done ? "Poništi fiskalizaciju" : "Označi kao fiskalizovano"}
      title={done && row.fiscalizedByName ? `Označio/la: ${row.fiscalizedByName}` : undefined}
      onCheckedChange={(v) => onToggle([{ source: row.source, sourceId: row.sourceId }], v === true)}
    />
  );
}

export function FlagBadge({ flag }: { flag: FiscalRow["flag"] }) {
  if (!flag) return null;
  return (
    <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
      {FLAG_LABEL[flag]}
    </span>
  );
}

export function MissingConfirmationBadge({ row, trackingStart }: { row: FiscalRow; trackingStart: string | null }) {
  if (!isConfirmationMissing(row, trackingStart)) return null;
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Potvrda nije poslana</span>
  );
}

export function AmountCell({ row }: { row: FiscalRow }) {
  return (
    <div>
      <div className="font-medium text-foreground">{formatCurrency(row.total)}</div>
      {row.base !== null && row.vat !== null && (
        <div className="text-xs text-muted-foreground">
          osn. {formatCurrency(row.base)} · PDV {formatCurrency(row.vat)}
        </div>
      )}
    </div>
  );
}

export function RowActions({ row, onResend }: { row: FiscalRow } & Pick<RowHandlers, "onResend">) {
  if (!hasConfirmation(row) || row.base === null) return null;
  return (
    <div className="flex justify-end gap-1">
      <Button asChild size="sm" variant="ghost">
        <a href={confirmationPdfUrl(row)} target="_blank" rel="noreferrer">
          PDF
        </a>
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onResend(row)}>
        Pošalji ponovo
      </Button>
    </div>
  );
}

export function MetaLine({ row }: { row: FiscalRow }) {
  return (
    <>
      {formatTimeShort(row.at)} · {row.kindLabel} · {METHOD_LABEL[row.method]}
    </>
  );
}
